// Data hooks for the Sessions roster page.
//
//   • useSessionBlockDefinitions — every session block document (the weekly
//     template). ~100 docs, no pagination. Also builds the byId index Week
//     mode uses to resolve a chip's home clinic and color.
//   • useSessionOccurrences — expanded per-date occurrences for the visible
//     week (overrides applied server-side), with a small optimistic overlay so
//     an override chip updates before the refetch lands.
//
// Both refetch on the same dual realtime bridge `use-slot-definitions.js`
// uses: explicit `publishCalendarRefresh()` mutations AND un-gated realtime
// appointment events (block mutations regenerate child slots, and auto-moved
// appointments ride the appointments.updated stream).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import {
  subscribeAppointmentChanges,
  subscribeAppointmentRefresh,
} from "@/lib/realtime/appointment-realtime-bridge";
import { fetchSessionBlocks, fetchSessionOccurrences } from "@/lib/api/admin-session-blocks";

function asTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function useRealtimeBumps() {
  const [bridgeVersion, setBridgeVersion] = useState(0);
  useEffect(() => {
    const bump = () => setBridgeVersion((value) => value + 1);
    const unsubscribeRefresh = subscribeAppointmentRefresh(bump);
    const unsubscribeChanges = subscribeAppointmentChanges(bump);
    return () => {
      unsubscribeRefresh();
      unsubscribeChanges();
    };
  }, []);
  return bridgeVersion;
}

function buildNameIndexes(doctorOptions, tenantOptions) {
  const doctorIndex = new Map();
  for (const option of Array.isArray(doctorOptions) ? doctorOptions : []) {
    const id = asTrimmedString(option?.id);
    if (id) doctorIndex.set(id, asTrimmedString(option?.name) || id);
  }
  const tenantIndex = new Map();
  for (const option of Array.isArray(tenantOptions) ? tenantOptions : []) {
    const id = asTrimmedString(option?.id);
    if (id) tenantIndex.set(id, asTrimmedString(option?.name) || id);
  }
  return { doctorIndex, tenantIndex };
}

function mapBlockItem(item, indexes) {
  const id = asTrimmedString(item?.id);
  if (!id) return null;
  const doctorId = asTrimmedString(item?.doctorId);
  const tenantId = asTrimmedString(item?.tenantId);
  return {
    id,
    doctorId,
    tenantId,
    calendarId: asTrimmedString(item?.calendarId),
    label: asTrimmedString(item?.label),
    startTime: asTrimmedString(item?.startTime),
    endTime: asTrimmedString(item?.endTime),
    timezone: asTrimmedString(item?.timezone),
    effectiveFrom: asTrimmedString(item?.effectiveFrom),
    recurrence: item?.recurrence && typeof item.recurrence === "object" ? item.recurrence : {},
    intervals: Array.isArray(item?.intervals) ? item.intervals : [],
    overrides: Array.isArray(item?.overrides) ? item.overrides : [],
    metadata: item?.metadata && typeof item.metadata === "object" ? item.metadata : {},
    colorHex: asTrimmedString(item?.metadata?.colorHex),
    doctorName: indexes.doctorIndex.get(doctorId) || doctorId,
    tenantName: indexes.tenantIndex.get(tenantId) || tenantId,
    raw: item,
  };
}

/**
 * All session block documents (the recurring template), refreshed on
 * `refreshKey` bumps and realtime slot/appointment churn.
 */
export function useSessionBlockDefinitions({
  refreshKey = 0,
  doctorOptions,
  tenantOptions,
  enabled = true,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bridgeVersion = useRealtimeBumps();

  useEffect(() => {
    if (!enabled) return undefined;
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    fetchSessionBlocks({ limit: 1000 })
      .then((response) => {
        if (!isMounted) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setItems([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [enabled, refreshKey, bridgeVersion]);

  const indexes = useMemo(
    () => buildNameIndexes(doctorOptions, tenantOptions),
    [doctorOptions, tenantOptions],
  );

  const blocks = useMemo(
    () => items.map((item) => mapBlockItem(item, indexes)).filter(Boolean),
    [items, indexes],
  );

  const blockById = useMemo(() => {
    const map = new Map();
    for (const block of blocks) map.set(block.id, block);
    return map;
  }, [blocks]);

  return { blocks, blockById, isLoading, error };
}

// Optimistic overlay entries expire after this long even if no refetch
// reconciles them (mirrors reconcilePendingOverrides in calendar-mutations).
const PENDING_TTL_MS = 15000;

function mapOccurrenceItem(item) {
  const blockId = asTrimmedString(item?.blockId);
  const date = asTrimmedString(item?.date);
  if (!blockId || !date) return null;
  const startAt = item?.start ? DateTime.fromISO(item.start) : null;
  const endAt = item?.end ? DateTime.fromISO(item.end) : null;
  const suppressed = Boolean(item?.suppressed);
  // A move produces two items for one (blockId, date-ish) pair — key the
  // suppressed source separately so both render.
  const key = `${blockId}:${date}:${suppressed ? "src" : "eff"}`;
  return {
    key,
    blockId,
    date,
    doctorId: asTrimmedString(item?.doctorId),
    tenantId: asTrimmedString(item?.tenantId),
    baseTenantId: asTrimmedString(item?.baseTenantId),
    calendarId: asTrimmedString(item?.calendarId),
    label: asTrimmedString(item?.label),
    startAt: startAt?.isValid ? startAt : null,
    endAt: endAt?.isValid ? endAt : null,
    timezone: asTrimmedString(item?.timezone),
    colorHex: asTrimmedString(item?.colorHex),
    intervalCount: Number(item?.intervalCount) || 0,
    suppressed,
    override: item?.override && typeof item.override === "object" ? item.override : null,
    raw: item,
  };
}

/**
 * Expanded per-date occurrences for [fromDate, toDate] (inclusive, Luxon
 * DateTimes). Exposes an optimistic overlay: `applyLocalOverride(blockId,
 * date, override)` marks the matching occurrence overridden immediately;
 * fetched results replace the overlay once the server catches up (or the TTL
 * expires).
 */
export function useSessionOccurrences({ fromDate, toDate, refreshKey = 0, enabled = true }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayVersion, setOverlayVersion] = useState(0);
  const bridgeVersion = useRealtimeBumps();
  const pendingRef = useRef(new Map()); // `${blockId}:${date}` -> {override|null(clear), expiresAt}

  const fromISO = fromDate?.isValid ? fromDate.toISODate() : "";
  const toISO = toDate?.isValid ? toDate.toISODate() : "";

  useEffect(() => {
    if (!enabled || !fromISO || !toISO) return undefined;
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    fetchSessionOccurrences({ from: fromISO, to: toISO })
      .then((response) => {
        if (!isMounted) return;
        const fetched = (Array.isArray(response?.items) ? response.items : [])
          .map(mapOccurrenceItem)
          .filter(Boolean);
        // Reconcile the optimistic overlay: drop entries the server already
        // reflects (or that expired).
        const now = Date.now();
        for (const [pendingKey, entry] of pendingRef.current) {
          if (entry.expiresAt <= now) {
            pendingRef.current.delete(pendingKey);
            continue;
          }
          const [blockId, date] = pendingKey.split("|");
          const serverRow = fetched.find((occ) => occ.blockId === blockId && occ.date === date);
          const serverAction = serverRow?.override?.action || "";
          const wantedAction = entry.override?.action || "";
          if (serverAction === wantedAction) pendingRef.current.delete(pendingKey);
        }
        setItems(fetched);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setItems([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [enabled, fromISO, toISO, refreshKey, bridgeVersion]);

  const applyLocalOverride = useCallback((blockId, date, override) => {
    pendingRef.current.set(`${blockId}|${date}`, {
      override: override || null,
      expiresAt: Date.now() + PENDING_TTL_MS,
    });
    setOverlayVersion((value) => value + 1);
  }, []);

  const revertLocalOverride = useCallback((blockId, date) => {
    pendingRef.current.delete(`${blockId}|${date}`);
    setOverlayVersion((value) => value + 1);
  }, []);

  const occurrences = useMemo(() => {
    void overlayVersion;
    if (pendingRef.current.size === 0) return items;
    const now = Date.now();
    const out = [];
    for (const occ of items) {
      const entry = pendingRef.current.get(`${occ.blockId}|${occ.date}`);
      if (!entry || entry.expiresAt <= now) {
        out.push(occ);
        continue;
      }
      const action = entry.override?.action || "";
      if (!action) {
        // Pending CLEAR (undo): render as plain.
        if (!occ.suppressed) out.push({ ...occ, override: null });
        else out.push({ ...occ, suppressed: false, override: null });
        continue;
      }
      if (action === "cancel") {
        if (!occ.suppressed) out.push({ ...occ, suppressed: true, override: { action: "cancel", ...entry.override } });
        continue;
      }
      if (action === "move") {
        if (!occ.suppressed) out.push({ ...occ, suppressed: true, override: { action: "move", sourceDate: occ.date, ...entry.override } });
        continue;
      }
      if (action === "relocate") {
        out.push({
          ...occ,
          tenantId: entry.override?.toTenantId || occ.tenantId,
          override: { action: "relocate", sourceDate: occ.date, ...entry.override },
        });
        continue;
      }
      out.push(occ);
    }
    return out;
  }, [items, overlayVersion]);

  return { occurrences, isLoading, error, applyLocalOverride, revertLocalOverride };
}
